import { useParams } from 'react-router';
import _ from '@lodash';
import { useState, createContext, useContext, useEffect, useMemo } from 'react';
import { subYears, addYears } from 'date-fns';

import MeasurementEditService from 'app/admin/measurement/service/MeasurementEditService';
import MeasurementTableService from 'app/admin/measurement/service/MeasurementTableService';
import ModelValidationEditService from 'app/admin/modelValidation/service/ModelValidationEditService';
import UnitOptimizerEditService from 'app/admin/unitOptimizer/service/UnitOptimizerEditService';
import ModelValidationLogTableService from 'app/admin/modelValidationLog/service/ModelValidationLogTableService';
import UnitOptimizerLogTableService from 'app/admin/unitOptimizerLog/service/UnitOptimizerLogTableService';
import OptimizationPipelineLogTableService from 'app/admin/optimizationPipelineLog/service/OptimizationPipelineLogTableService';
import OptimizationPipelineEditService from '../service/OptimizationPipelineEditService';
import PiWriterEditService from 'app/admin/piWriter/service/PiWriterEditService';
import PiWriterLogEditService from 'app/admin/piWriterLog/service/PiWriterLogEditService';
import EdtDynamicCheckEditService from 'app/admin/edtDynamicCheck/service/EdtDynamicCheckEditService';
import EdtDynamicCheckLogTableService from 'app/admin/edtDynamicCheckLog/service/EdtDynamicCheckLogTableService';
import OptimizationPipelineTableService from '../service/OptimizationPipelineTableService';
import OptimizationPipelineLogEditService from 'app/admin/optimizationPipelineLog/service/OptimizationPipelineLogEditService';

// import measurementTableControl_23 from '../measurementTableControl_23.json';
// import measurementTableControl_26 from '../measurementTableControl_26.json';

export const OptimizationPipelineContext = createContext(null);

export const useOptimizationPipeline = () => { return useContext(OptimizationPipelineContext); };

export default function OptimizationPipelineProvider(props) {
	const { id } = useParams();

	const [sidebar, setSidebar] = useState(null);
	const [popup, setPopup] = useState(null);

	const [ts, setTs] = useState(null);

	const [timestamps, setTimestamps] = useState(null);
	const [optimizationPipelineData, setOptimizationPipelineData] = useState(null);
	const [optimizationPipelineDetails, setOptimizationPipelineDetails] = useState(null);
	const [optimizationPipelineLogData, setOptimizationPipelineLogData] = useState(null);

	const [mergedMeasurementData, setMergedMeasurementData] = useState(null);
	const [mergedMeasurementDataList, setMergedMeasurementDataList] = useState(null);

	const [cleanMeasurementData, setCleanMeasurementData] = useState(null);
	const [cleanMeasurementDataList, setCleanMeasurementDataList] = useState(null);

	const [modelValidationData, setModelValidationData] = useState(null);
	const [modelValidationLogDataList, setModelValidationLogDataList] = useState(null);
	const [modelValidationLogDataListByTs, setModelValidationLogDataListByTs] = useState(null);

	const [unitOptimizerData, setUnitOptimizerData] = useState(null);
	const [unitOptimizerLogDataList, setUnitOptimizerLogDataList] = useState(null);

	const [piWriterData, setPiWriterData] = useState(null);
	const [piWriterLogData, setPiWriterLogData] = useState(null);

	const [edtDynamicCheckData, setEdtDynamicCheckData] = useState(null);
	const [edtDynamicCheckLogData, setEdtDynamicCheckLogData] = useState(null);

	const [dynamicValidationSettings, setDynamicValidationSettings] = useState(null);

	useEffect(() => {
		if (id && id !== 'new') {
			OptimizationPipelineEditService.getData(id).then(resp => setOptimizationPipelineData(resp));
			// OptimizationPipelineTableService.getDetails(id).then(resp => setOptimizationPipelineDetails(resp));
		} else {
			setOptimizationPipelineData(null);
			setOptimizationPipelineDetails(null);
		}
	}, [id]);

	useEffect(() => {
		if (optimizationPipelineData) {
			OptimizationPipelineLogTableService.getTimestampsInDateInterval(
				optimizationPipelineData.optimizationPipelineLog,
				subYears(new Date(), 1),
				addYears(new Date(), 1)
			).then(resp => setTimestamps(resp.data));

			if (optimizationPipelineData.mergedMeasurement) {
				MeasurementEditService.getData(optimizationPipelineData.mergedMeasurement).then(resp => setMergedMeasurementData(resp));
				MeasurementTableService.getData(optimizationPipelineData.mergedMeasurement, null, null).then(resp => setMergedMeasurementDataList(resp));
			}

			if (optimizationPipelineData.cleanMeasurement) {
				MeasurementEditService.getData(optimizationPipelineData.cleanMeasurement).then(resp => setCleanMeasurementData(resp));
				MeasurementTableService.getData(optimizationPipelineData.cleanMeasurement, null, null).then(resp => setCleanMeasurementDataList(resp));
			}

			if (optimizationPipelineData.modelValidation) ModelValidationEditService.getData(optimizationPipelineData.modelValidation).then(resp => setModelValidationData(resp));
			if (optimizationPipelineData.unitOptimizer) UnitOptimizerEditService.getData(optimizationPipelineData.unitOptimizer).then(resp => setUnitOptimizerData(resp));
			if (optimizationPipelineData.piWriter) PiWriterEditService.getData(optimizationPipelineData.piWriter).then(resp => setPiWriterData(resp));
			if (optimizationPipelineData.edtDynamicCheck) EdtDynamicCheckEditService.getData(optimizationPipelineData.edtDynamicCheck).then(resp => setEdtDynamicCheckData(resp));
			// if (optimizationPipelineData.piWriter) PiWriterEditService.getData(optimizationPipelineData.piWriter).then(resp => setPiWriterData(resp));

		} else {
			setMergedMeasurementData(null);
			setMergedMeasurementDataList(null);
			setCleanMeasurementData(null);
			setCleanMeasurementDataList(null);
			setPiWriterData(null);
		}
	}, [optimizationPipelineData]);

	useEffect(() => {
		if (modelValidationData && ts) {
			ModelValidationLogTableService.getData(modelValidationData.modelValidationLog, null, null).then(resp => setModelValidationLogDataList(resp));
			ModelValidationLogTableService.getDataByTs(modelValidationData.modelValidationLog, ts).then(resp => setModelValidationLogDataListByTs(resp));
		} else {
			setModelValidationLogDataList(null);
		}
	}, [modelValidationData, ts]);

	useEffect(() => {
		if (unitOptimizerData)
			UnitOptimizerLogTableService.getData(unitOptimizerData.unitOptimizerLog, null, null).then(resp => setUnitOptimizerLogDataList(resp));
		else
			setUnitOptimizerLogDataList(null);
	}, [unitOptimizerData]);

	useEffect(() => {
		if (piWriterData && piWriterData.piWriterLog)
			PiWriterLogEditService.getData(piWriterData.piWriterLog, null, null).then(resp => setPiWriterLogData(resp));
		else
			setPiWriterLogData(null);
	}, [piWriterData]);

	useEffect(() => {
		if (edtDynamicCheckData && edtDynamicCheckData.log && ts)
			EdtDynamicCheckLogTableService.getDataByTimestamp(edtDynamicCheckData.log, ts).then(resp => setEdtDynamicCheckLogData(resp));
		else
			setEdtDynamicCheckLogData(null);
	}, [edtDynamicCheckData, ts]);

	useEffect(() => {
		setDynamicValidationSettings(null);
	}, [ts]);

	const dataValidationSkipReasons = useMemo(() => getDataValidationSkipReasons(mergedMeasurementDataList, ts), [mergedMeasurementDataList, ts]);
	const isDataValidationSkipped = dataValidationSkipReasons.length > 0;

	const value = {
		id,
		ts,
		setTs,
		sidebar,
		setSidebar,
		popup,
		setPopup,
		optimizationPipelineData,
		mergedMeasurementData,
		mergedMeasurementDataList,
		cleanMeasurementData,
		cleanMeasurementDataList,
		modelValidationData,
		modelValidationLogDataList,
		modelValidationLogDataListByTs,
		unitOptimizerData,
		unitOptimizerLogDataList,
		edtDynamicCheckData,
		edtDynamicCheckLogData,
		timestamps,
		dynamicValidationSettings,
		setDynamicValidationSettings,
		dataValidationSkipReasons,
		isDataValidationSkipped
	};

	return <OptimizationPipelineContext.Provider value={value}>{props.children}</OptimizationPipelineContext.Provider>;
}

/* utils */

export function getDataValidationSkipReasons(mergedMeasurementDataList, ts) {
	if (!Array.isArray(mergedMeasurementDataList) || !ts) return [];

	const target = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
	if (Number.isNaN(target)) return [];

	const entry = mergedMeasurementDataList.find(e => {
		const rawTs = e?.ts || e?.meta?.ts || e?.timestamp;
		const t = rawTs ? new Date(rawTs).getTime() : NaN;
		return !Number.isNaN(t) && t === target;
	});

	if (!entry) return [];

	const od = entry?.meta?.outlier_detection || entry?.outlier_detection;
	if (!od) return [];

	const skipped = od?.skipped_because_of;
	if (!skipped) return [];

	if (Array.isArray(skipped)) {
		return skipped
			.map(v => (v == null ? '' : String(v)))
			.filter(Boolean);
	}

	if (typeof skipped === 'string') {
		const s = skipped.trim();
		return s ? [s] : [];
	}

	return [];
}